import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import { ADFPaymentRequest, PaymentType } from '@/models/Payment';
import { ProgramNames } from '@/models/Program';
import { QuestionReviewProps } from '@/models/QuestionReview';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';
import { ApplicationTypes } from '@/models/ApplicationType';
import { Star, StarStatus } from '@/models/Star';

import ApplicationApprovalModal from './ApplicationApprovalModal';
import useStar from '@/hooks/useStar';
import {
  getApplicationReviewerRoles,
  isTreeCityWorldGrowthAwardApplication,
} from '@/utils/applicationUtil';
import { TreeCityWorldGrowthAwardApplication } from '@/models/Application';
import {
  isLastStandardInList,
  shouldBlockLastStandardNavigation,
} from '@/utils/standardNavigationUtil';

type ButtonVariant = 'primary' | 'secondary';

type ButtonConfig = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  loading?: boolean;
  onClick: () => void;
  variant: ButtonVariant;
  disabled: boolean;
  tooltip?: string;
};

function leadingDebounce<TArgs extends unknown[]>(
  func: (...args: TArgs) => void,
  timeout = 500
): (...args: TArgs) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: TArgs) => {
    if (timer === null) func(...args);
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
    }, timeout);
  };
}

function paymentSectionCompleted(
  payRequest: ADFPaymentRequest,
  applicationStatus: ApplicationStatus | null,
  programId: ProgramNames
) {
  if (payRequest == null) return false;
  if (programId !== ProgramNames.TreeLineUSA) return true;
  if (applicationStatus !== ApplicationStatus.InProgress) return true;

  switch (payRequest.paymentType) {
    case PaymentType.OnAccount:
      return true;
    case PaymentType.CreditCard:
      return (
        payRequest.creditCardToken !== '' &&
        payRequest.creditCardToken !== null &&
        payRequest.creditCardExpirationDate !== '' &&
        payRequest.creditCardExpirationDate !== null &&
        payRequest.billingContact.name !== '' &&
        payRequest.billingContact.name !== null &&
        payRequest.billingContact.address !== '' &&
        payRequest.billingContact.address !== null &&
        payRequest.billingContact.city !== '' &&
        payRequest.billingContact.city !== null &&
        payRequest.billingContact.region !== '' &&
        payRequest.billingContact.region !== null &&
        payRequest.billingContact.postalCode !== '' &&
        payRequest.billingContact.postalCode !== null &&
        payRequest.billingContact.country !== '' &&
        payRequest.billingContact.country !== null
      );
    default:
      return true;
  }
}

export default function ApplicationButtons({
  submitClicked,
  onSubmitClickedChange,
  onPaymentError,
  applicationComments,
  paymentRequest,
}: {
  submitClicked: boolean;
  onSubmitClickedChange: (clicked: boolean) => void;
  onPaymentError: (message: string | null) => void;
  applicationComments: QuestionReviewProps[];
  paymentRequest: ADFPaymentRequest;
}) {
  const navigate = useNavigate();
  const {
    isPreviewMode,
    currentApplication,
    currentStandardId,
    makingApiRequest,
    canEdit,
    allComments,
    standardError,
    sortedStandards,
    isFirstStandard,
    updateCurrentStandardId,
    updateCurrentApplication,
    star,
  } = useContext(CurrentApplicationContext);

  const { selectedProgramId, portalOpen } = useContext(SelectedProgramContext);
  const { updateApplicationStatus } = useContext(DashboardContext);
  const { setAlert } = useContext(SnackbarContext);
  const { userType, userCurrent } = useContext(UsersContext);
  const { translatedText } = useContext(LocaleContext);
  const { postApplicationStatus, postSubmit, postResubmit } = useApplication();
  const { addStar, updateStarStatus } = useStar();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isUpdatingStar, setIsUpdatingStar] = useState<boolean>(false);

  const onResubmitRef = useRef(resubmitApp);
  onResubmitRef.current = resubmitApp;
  const debouncedResubmit = useRef(leadingDebounce(() => onResubmitRef.current(), 3000));

  const onSubmitRef = useRef(submitApp);
  onSubmitRef.current = submitApp;
  const debouncedSubmit = useRef(leadingDebounce(() => onSubmitRef.current(), 3000));

  const onApproveRef = useRef(approveApplication);
  onApproveRef.current = approveApplication;
  const debouncedApprove = useRef(leadingDebounce(() => onApproveRef.current(), 3000));

  const onSetCurrentStandardStarStatusRef = useRef(setCurrentStandardStarStatus);
  onSetCurrentStandardStarStatusRef.current = setCurrentStandardStarStatus;
  const debouncedSetCurrentStandardStarStatus = useRef(
    leadingDebounce(
      (status: StarStatus, createIfMissing: boolean) =>
        onSetCurrentStandardStarStatusRef.current(status, createIfMissing),
      3000
    )
  );

  const hasIncompleteStandards = Object.values(standardError).some((el: boolean) => el == false);
  const hasUnresolvedComments = applicationComments.some((comment) => !comment.resolved);
  const { isReviewerProgram, isRegionalReviewer, isStateReviewer } = getApplicationReviewerRoles(
    userType,
    currentApplication.status,
    selectedProgramId
  );

  const canSendBackToApplicant =
    !submitClicked &&
    currentApplication.applicationTypeId != null &&
    portalOpen[currentApplication.applicationTypeId] &&
    canEdit &&
    (allComments ?? []).length > 0 &&
    !applicationComments.every((comment) => comment.resolved);

  const showResubmitButton =
    !isPreviewMode && currentApplication.status == ApplicationStatus.InRevision;

  const showStarApprovalButton =
    !isPreviewMode &&
    currentApplication.programId == ProgramNames.TreeCitiesWorld &&
    currentApplication.applicationTypeId == ApplicationTypes.GrowthAward &&
    userCurrent.userType != UserType.Applicant &&
    currentApplication.status != ApplicationStatus.InProgress;

  const showSubmitButton =
    !isPreviewMode &&
    !showResubmitButton &&
    ((userType == UserType.Applicant && canEdit) ||
      (userType != null &&
        [UserType.RegionalCoordinator, UserType.StateCoordinator, UserType.Adf].includes(
          userType
        ) &&
        currentApplication.status != null &&
        currentApplication.status == ApplicationStatus.InProgress));

  const currentStatus = currentApplication.status;

  const isNationalProgrammeLeadReviewer =
    userType == UserType.NationalProgrammeLead &&
    currentStatus != null &&
    [ApplicationStatus.PendingNationalReview].includes(currentStatus) &&
    isReviewerProgram;

  const isAdfReviewer =
    userType == UserType.Adf &&
    currentStatus != null &&
    [
      ApplicationStatus.PendingRegionalReview,
      ApplicationStatus.PendingStateReview,
      ApplicationStatus.PendingNationalReview,
      ApplicationStatus.PendingAdfReview,
    ].includes(currentStatus);

  const showReviewButtons =
    !isPreviewMode &&
    !showResubmitButton &&
    !showSubmitButton &&
    (isRegionalReviewer || isStateReviewer || isNationalProgrammeLeadReviewer || isAdfReviewer);

  const buttonConfigs: ButtonConfig[] = [];

  const currentStandardIndex = sortedStandards.findIndex((s) => s.standardId === currentStandardId);
  const nextStandard = sortedStandards[currentStandardIndex + 1];

  const currentIsLastStandard = isLastStandardInList(sortedStandards, currentStandardId);

  buttonConfigs.push({
    id: 'previous-standard',
    label: translatedText.applicationBody.tooltipPreviousStandard,
    onClick: handleBack,
    variant: 'secondary',
    disabled: isFirstStandard,
    tooltip: translatedText.applicationBody.tooltipPreviousStandard,
  });

  if (showStarApprovalButton) {
    if (star != null && star.starStatusId === StarStatus.Awarded) {
      buttonConfigs.push({
        id: 'star-removal',
        label: translatedText.applicationBody.starRemovalButton,
        icon: <StarIcon />,
        loading: isUpdatingStar,
        onClick: () => debouncedSetCurrentStandardStarStatus.current(StarStatus.NotAwarded, false),
        variant: 'secondary',
        disabled: submitClicked || !canEdit,
      });
    } else {
      buttonConfigs.push({
        id: 'star-approval',
        label: translatedText.applicationBody.starApprovalButton,
        icon: <StarBorderIcon />,
        loading: isUpdatingStar,
        onClick: () => debouncedSetCurrentStandardStarStatus.current(StarStatus.Awarded, true),
        variant: 'secondary',
        disabled: submitClicked || !canEdit,
      });
    }
  }

  if (!currentIsLastStandard) {
    const nextIsLastStandard =
      nextStandard != null ? isLastStandardInList(sortedStandards, nextStandard.standardId) : false;

    const disableNextStandard = shouldBlockLastStandardNavigation({
      isTargetingLastStandard: nextIsLastStandard,
      applicationTypeId: currentApplication.applicationTypeId,
      programId: currentApplication.programId,
      status: currentApplication.status,
      standardError,
      canEdit,
    });

    buttonConfigs.push({
      id: 'next-standard',
      label: translatedText.applicationBody.tooltipNextStandard,
      onClick: handleNext,
      variant: 'primary',
      disabled: disableNextStandard,
      tooltip: disableNextStandard
        ? translatedText.applicationBody.tooltipRequiredQuestions
        : translatedText.applicationBody.tooltipNextStandard,
    });
  }

  if (currentIsLastStandard) {
    if (showResubmitButton) {
      buttonConfigs.push({
        id: 'resubmit',
        label: translatedText.applicationBody.resubmitButton,
        onClick: debouncedResubmit.current,
        variant: 'primary',
        disabled:
          submitClicked ||
          (userType != UserType.Adf &&
            currentApplication.applicationTypeId != null &&
            !portalOpen[currentApplication.applicationTypeId]) ||
          hasIncompleteStandards,
      });
    }

    if (showSubmitButton) {
      buttonConfigs.push({
        id: 'submit',
        label: submitClicked
          ? translatedText.applicationBody.submitMessage
          : translatedText.applicationBody.submitButton,
        onClick: debouncedSubmit.current,
        variant: 'primary',
        disabled:
          submitClicked ||
          makingApiRequest ||
          hasIncompleteStandards ||
          !paymentSectionCompleted(
            paymentRequest,
            currentApplication.status,
            currentApplication.programId
          ),
      });
    }

    if (showReviewButtons) {
      buttonConfigs.push({
        id: 'sendBack',
        label: translatedText.applicationBody.sendBackToApplicantWithComments,
        onClick: sendBacktoApplicant,
        variant: 'secondary',
        disabled: !canSendBackToApplicant,
        tooltip: translatedText.applicationBody.tooltipSendBackRequiresComment,
      });

      buttonConfigs.push({
        id: 'approve',
        label: translatedText.applicationBody.approveApplicationButton,
        onClick: debouncedApprove.current,
        variant: 'primary',
        disabled: submitClicked || !canEdit || hasIncompleteStandards || hasUnresolvedComments,
        tooltip: `${translatedText.applicationBody.tooltipApproveSendsToNextReviewStage} ${
          hasUnresolvedComments
            ? translatedText.applicationBody.tooltipApproveWithUnresolvedComments
            : translatedText.applicationBody.tooltipApproveBeforeSubmit
        }`,
      });
    }
  }

  if (buttonConfigs.length === 0) return <div></div>;

  return (
    <>
      {buttonConfigs.map((buttonConfig) => {
        const button = (
          <Button
            disabled={buttonConfig.disabled}
            onClick={buttonConfig.onClick}
            variant={buttonConfig.variant}
            size='large'
          >
            {buttonConfig.loading === true && (
              <span style={{ marginRight: '8px', display: 'flex' }}>
                <CircularProgress size={18} color='inherit' />
              </span>
            )}
            {buttonConfig.loading !== true && buttonConfig.icon != null && (
              <span style={{ marginRight: '8px', display: 'flex' }}>{buttonConfig.icon}</span>
            )}
            {buttonConfig.label}
          </Button>
        );

        if (buttonConfig.tooltip == null) {
          return <span key={buttonConfig.id}>{button}</span>;
        }

        return (
          <Tooltip key={buttonConfig.id} title={buttonConfig.tooltip}>
            <span>{button}</span>
          </Tooltip>
        );
      })}

      {modalOpen && <ApplicationApprovalModal open={modalOpen} onClose={handleClose} />}
    </>
  );

  function handleClose() {
    setModalOpen(false);
  }

  async function submitApp() {
    onSubmitClickedChange(true);

    const mergedAddress =
      paymentRequest.billingContact.address2 != null &&
      paymentRequest.billingContact.address2 !== ''
        ? `${paymentRequest.billingContact.address} ${paymentRequest.billingContact.address2}`
        : paymentRequest.billingContact.address;

    const submittedRequest: ADFPaymentRequest = {
      ...paymentRequest,
      billingContact: { ...paymentRequest.billingContact, address: mergedAddress, address2: '' },
    };

    try {
      const updatedApplicationStatus = await postSubmit(currentApplication.id, submittedRequest);

      updateApplicationStatus(updatedApplicationStatus, currentApplication.id);

      setTimeout(() => {
        navigate('/submit-confirmation', {
          state: {
            applicationId: currentApplication.id,
          },
        });
        onSubmitClickedChange(false);
      }, 1000);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setAlert({
        message: `Error submitting application: ${errorMessage}`,
        severity: 'error',
      });
      onPaymentError(errorMessage);
      onSubmitClickedChange(false);
    }
  }

  function resubmitApp() {
    onSubmitClickedChange(true);

    postResubmit(currentApplication.id)
      .then((updatedApplicationStatus: ApplicationStatus) => {
        updateApplicationStatus(updatedApplicationStatus, currentApplication.id);
        setTimeout(() => {
          navigate('/submit-confirmation', {
            state: {
              applicationId: currentApplication.id,
            },
          });
          onSubmitClickedChange(false);
        }, 1000);
      })
      .catch((error: Error) => {
        onSubmitClickedChange(false);
        setAlert({
          message: `Error resubmitting application: ${error.message}`,
          severity: 'error',
        });
      });
  }

  async function sendBacktoApplicant() {
    onSubmitClickedChange(true);

    try {
      const updatedApplicationStatus = await postApplicationStatus(currentApplication.id, true);

      updateApplicationStatus(updatedApplicationStatus, currentApplication.id);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error: unknown) {
      setAlert({
        message: `Error sending application back to applicant: ${(error as Error).message}`,
        severity: 'error',
      });
      onSubmitClickedChange(false);
    }
  }

  async function approveApplication() {
    if (
      (userType == UserType.StateCoordinator &&
        currentApplication.status === ApplicationStatus.PendingStateReview &&
        !modalOpen) ||
      (userType == UserType.Adf && !modalOpen)
    ) {
      setModalOpen(true);
    } else {
      onSubmitClickedChange(true);

      const ret: ApplicationStatus | null = await postApplicationStatus(
        currentApplication.id,
        false
      );
      if (ret != null) {
        updateApplicationStatus(ret, currentApplication.id);
        setTimeout(() => {
          navigate('/dashboard');
        }, 1000);
      } else {
        setAlert({
          message: `Error approving application`,
          severity: 'error',
        });
        onSubmitClickedChange(false);
      }
    }
  }

  function handleNext() {
    if (nextStandard?.standardId != null) {
      updateCurrentStandardId(nextStandard.standardId);
    }
  }

  function handleBack() {
    const previousStandard = sortedStandards[currentStandardIndex - 1];
    if (previousStandard?.standardId != null) {
      updateCurrentStandardId(previousStandard.standardId);
    }
  }

  async function setCurrentStandardStarStatus(newStarStatus: StarStatus, createIfMissing: boolean) {
    if (!isTreeCityWorldGrowthAwardApplication(currentApplication)) return;
    if (isUpdatingStar) return;

    const stars: Star[] = currentApplication.stars;

    try {
      setIsUpdatingStar(true);

      const starToUpdate = stars.find(
        (s: Star) => s.standardId === currentStandardId && s.applicationId === currentApplication.id
      );

      let updatedStar: Star | null = null;
      if (starToUpdate?.id != null) {
        updatedStar = await updateStarStatus(starToUpdate.id, newStarStatus);
      } else if (createIfMissing) {
        const newStar: Star = {
          applicationId: currentApplication.id,
          starStatusId: newStarStatus,
          partnerId: currentApplication.partnerId,
          standardId: currentStandardId,
        };

        updatedStar = await addStar(newStar);
      }

      if (updatedStar == null) return;

      const updatedStars = stars.some((s) => s.id === updatedStar.id)
        ? stars.map((s) => (s.id === updatedStar.id ? updatedStar : s))
        : [...stars, updatedStar];

      const updatedApplication: TreeCityWorldGrowthAwardApplication = {
        ...currentApplication,
        stars: updatedStars,
      };

      updateCurrentApplication(updatedApplication);
    } catch (error: unknown) {
      setAlert({
        message: `Error updating star status: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setIsUpdatingStar(false);
    }
  }
}
